import { inject, shallowRef } from 'vue';

/** FeedScrollRoot 向子树注入本地 scroll ref（keep-alive 下不受全局 unregister 影响） */
export const FEED_SCROLL_INJECT_KEY = Symbol('feedScrollElement');

/** FeedScrollRoot 向子树注入 restoreKey（BackToTop 等需重置 anchor） */
export const FEED_SCROLL_RESTORE_KEY = Symbol('feedScrollRestoreKey');

/** 当前激活 Feed 页的滚动容器（供 composable 监听 scroll / scrollToTop） */
export const feedScrollElement = shallowRef(null);

/** keep-alive 返回后虚拟列表 remeasure 尚未稳定，此期间禁止 loadMore */
let feedScrollLayoutSettleUntil = 0;

export function markFeedScrollLayoutSettling(ms = 450) {
  feedScrollLayoutSettleUntil = Date.now() + ms;
}

export function isFeedScrollLayoutSettling() {
  return Date.now() < feedScrollLayoutSettleUntil;
}

export function registerFeedScrollElement(el) {
  feedScrollElement.value = el;
}

export function unregisterFeedScrollElement(el) {
  if (feedScrollElement.value === el) feedScrollElement.value = null;
}

/** 当前激活 Feed 页的全局 scroller（BackToTop 等壳层组件用） */
export function useFeedScrollElement() {
  return feedScrollElement;
}

/**
 * Feed 页 scroll 生命周期 composable 专用：优先 inject 本地 scroller，
 * 避免 keep-alive 下监听全局 feedScrollElement 导致 Home/Explore 互相触发 loadMore。
 */
export function useFeedPageScrollElement() {
  const injected = inject(FEED_SCROLL_INJECT_KEY, null);
  return injected ?? feedScrollElement;
}

export function useFeedScrollRestoreKey() {
  return inject(FEED_SCROLL_RESTORE_KEY, null);
}

/**
 * 虚拟列表专用：优先使用 FeedScrollRoot inject 的本地 scroller，避免路由切换时全局 ref 为空导致空白。
 */
export function useFeedListScrollElement() {
  const injected = inject(FEED_SCROLL_INJECT_KEY, null);
  return {
    injectedScrollRef: injected,
    globalScrollRef: feedScrollElement,
  };
}

/** 将 Feed 滚动容器滚到顶部；无容器时回退 window */
export function scrollFeedContainerToTop() {
  clearPendingFeedScrollRestore();
  const el = feedScrollElement.value;
  if (el) {
    el.scrollTop = 0;
    return;
  }
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }
}

/** 解析虚拟列表实际使用的 scroll 元素 */
export function resolveFeedListScrollElement(
  injectedScrollRef,
  globalScrollRef = feedScrollElement
) {
  return injectedScrollRef?.value || globalScrollRef?.value || null;
}

/** keep-alive 各 Feed 路由独立保存 scrollTop（display:none 后浏览器会丢失） */
const feedScrollAnchors = new Map();
let pendingFeedScrollRestore = 0;

export function saveFeedScrollAnchor(routeKey, scrollTop) {
  const key = String(routeKey ?? '').trim() || 'default';
  const top = Math.max(0, Number(scrollTop) || 0);
  feedScrollAnchors.set(key, top);
}

export function resetFeedScrollAnchor(routeKey) {
  saveFeedScrollAnchor(routeKey, 0);
}

export function peekFeedScrollAnchor(routeKey) {
  const key = String(routeKey ?? '').trim() || 'default';
  return feedScrollAnchors.get(key) ?? 0;
}

export function takeFeedScrollAnchor(routeKey) {
  const top = peekFeedScrollAnchor(routeKey);
  pendingFeedScrollRestore = top;
  return top;
}

export function getPendingFeedScrollRestore() {
  return pendingFeedScrollRestore;
}

export function clearPendingFeedScrollRestore() {
  pendingFeedScrollRestore = 0;
}

/** 登出 / 换账号时清空 keep-alive 滚动锚点，避免恢复到上一用户的 scrollTop */
export function clearAllFeedScrollAnchors() {
  feedScrollAnchors.clear();
  clearPendingFeedScrollRestore();
}

/** remeasure / layout settle 后再次锚定 scrollTop（内容未撑开时重试） */
export function restoreFeedScrollAnchor(el, anchor, attempt = 0) {
  if (!el || anchor <= 0) {
    clearPendingFeedScrollRestore();
    return;
  }

  const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
  const target = Math.min(anchor, maxScroll);
  el.scrollTop = target;

  const settled = Math.abs(el.scrollTop - target) <= 1;
  const canReachAnchor = maxScroll + 1 >= anchor;

  if (settled && (canReachAnchor || attempt >= 16)) {
    clearPendingFeedScrollRestore();
    return;
  }

  if (attempt >= 16) {
    clearPendingFeedScrollRestore();
    return;
  }

  requestAnimationFrame(() => restoreFeedScrollAnchor(el, anchor, attempt + 1));
}

/** 开发环境调试：Console 执行 window.__dsFeedScrollDebug?.() */
export function getFeedScrollDebugSnapshot() {
  const el = feedScrollElement.value;
  return {
    anchors: Object.fromEntries(feedScrollAnchors.entries()),
    pendingRestore: pendingFeedScrollRestore,
    scroller: el
      ? {
          scrollTop: el.scrollTop,
          scrollHeight: el.scrollHeight,
          clientHeight: el.clientHeight,
        }
      : null,
    windowScrollY:
      typeof window !== 'undefined' ? window.scrollY || document.documentElement.scrollTop || 0 : 0,
  };
}
